import {
  BASE_NAV_ITEM_CONFIG,
  BASE_NAV_ITEM_KEYS,
  BaseNavItemKey,
  DEFAULT_BASE_NAV_POSITIONS,
  DEFAULT_CUSTOM_LINK_START_POSITION,
  DEFAULT_EXTERNAL_LINK_START_POSITION,
  isBuiltInPageKey,
} from "../../../data/navigation";
import {
  VenuePageCustomLink,
  VenuePageExternalLink,
  VenuePageNavItemRow,
  VenuePagesRow,
} from "../../../data/venue_pages";

import { PageNavLink } from "@punchup/ui-common/components/common";
import { VenuePageNavGroupRow } from "../../../data/venue_pages_nav_groups";

export type VenuePageUrlBase = "/v/" | "/venue/" | "/";

export interface ComputedNavItem {
  label: string;
  href: string;
  title?: string;
  type: "internal" | "external";
  position: number;
  itemType: "base" | "custom" | "external" | "group";
  itemKey: string;
  // For grouped children, the id of the group they belong to. Top-level
  // entries (including groups themselves) carry `null`.
  parentGroupId?: string | null;
}

export interface NavTreeNode extends ComputedNavItem {
  children?: ComputedNavItem[];
}

interface ComputeNavItemsParams {
  venuePage: VenuePagesRow;
  customLinks: VenuePageCustomLink[];
  externalLinks: VenuePageExternalLink[];
  navItems: VenuePageNavItemRow[];
  navGroups: VenuePageNavGroupRow[];
  hasFAQs: boolean;
  hasMenu: boolean;
  urlBase: VenuePageUrlBase;
}

/**
 * Determines if a base nav item should be visible based on venue page data.
 * Some items are conditionally shown based on venue configuration.
 */
function isBaseItemVisible(
  key: BaseNavItemKey,
  venuePage: VenuePagesRow,
  hasFAQs: boolean,
  hasMenu: boolean,
  override: VenuePageNavItemRow | undefined
): boolean {
  const {
    is_producer,
    is_location_hidden,
    location_ids,
    phone,
    email,
    external_site_info,
  } = venuePage;

  switch (key) {
    case BASE_NAV_ITEM_KEYS.HOME:
    case BASE_NAV_ITEM_KEYS.CALENDAR:
    case BASE_NAV_ITEM_KEYS.ABOUT:
      return true;

    case BASE_NAV_ITEM_KEYS.FREE_SHOWS:
    case BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT:
    case BASE_NAV_ITEM_KEYS.SUBSCRIBE:
      // Opt-in pages are hidden from the navbar by default and surface only
      // when the venue has explicitly added a venue_pages_nav_items row. The
      // page still exists at its URL regardless; the nav row controls
      // visibility, and (for FREE_SHOWS / SHOWS_TONIGHT) a matching
      // venue_pages_custom_links row supplies the editorial title/body.
      return override !== undefined;

    case BASE_NAV_ITEM_KEYS.MAP_PARKING: {
      const hideMapParking =
        is_location_hidden ||
        is_producer ||
        !location_ids ||
        location_ids.length === 0;
      return !hideMapParking;
    }

    case BASE_NAV_ITEM_KEYS.MENU:
      return hasMenu;

    case BASE_NAV_ITEM_KEYS.FAQS:
      return hasFAQs;

    case BASE_NAV_ITEM_KEYS.CONTACT:
      return Boolean(phone || email);

    case BASE_NAV_ITEM_KEYS.SOCIALS: {
      const socialInfo = external_site_info?.find((info) => {
        if (typeof info === "object" && info !== null) {
          return (info as { type?: string }).type === "social";
        }
        return false;
      });
      const socialLinks =
        socialInfo && typeof socialInfo === "object" && "links" in socialInfo
          ? (socialInfo.links as Record<string, string>)
          : {};
      const hasSocialLinks = Object.values(socialLinks).some(
        (link): link is string => typeof link === "string" && link.trim() !== ""
      );
      return hasSocialLinks;
    }

    default:
      return true;
  }
}

/**
 * Generates the href for a base nav item.
 */
function getBaseItemHref(
  key: BaseNavItemKey,
  slug: string,
  urlBase: VenuePageUrlBase
): string {
  const basePath = getBasePath(urlBase, slug);

  switch (key) {
    case BASE_NAV_ITEM_KEYS.HOME:
      return `${basePath}/`;
    case BASE_NAV_ITEM_KEYS.CALENDAR:
      return `${basePath}/calendar`;
    case BASE_NAV_ITEM_KEYS.MAP_PARKING:
      return `${basePath}/map`;
    case BASE_NAV_ITEM_KEYS.MENU:
      return `${basePath}/menu`;
    case BASE_NAV_ITEM_KEYS.FAQS:
      return `${basePath}/faqs`;
    case BASE_NAV_ITEM_KEYS.CONTACT:
      return `${basePath}/contact`;
    case BASE_NAV_ITEM_KEYS.ABOUT:
      return `${basePath}/about`;
    case BASE_NAV_ITEM_KEYS.SOCIALS:
      return `${basePath}/socials`;
    case BASE_NAV_ITEM_KEYS.SUBSCRIBE:
      return `${basePath}/subscribe`;
    case BASE_NAV_ITEM_KEYS.FREE_SHOWS:
      return `${basePath}/free-shows`;
    case BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT:
      return `${basePath}/shows-tonight`;
    default:
      return basePath;
  }
}

/**
 * Computes the final list of navigation items by merging:
 * 1. Base items (with default or overridden positions)
 * 2. Custom internal links
 * 3. External links
 * 4. Dropdown groups
 *
 * The result is sorted by position and filtered for visibility.
 *
 * `navGroups` is required: a render surface that fetches `navItems` but not
 * `navGroups` would drop group entries and orphan their children. Callers
 * pass `[]` only when the surface has no groups.
 */
export function computeNavItems({
  venuePage,
  customLinks,
  externalLinks,
  navItems,
  navGroups,
  hasFAQs,
  hasMenu,
  urlBase,
}: ComputeNavItemsParams): ComputedNavItem[] {
  const { slug } = venuePage;
  const items: ComputedNavItem[] = [];

  // Create lookup maps for overrides
  const baseOverrides = new Map<string, VenuePageNavItemRow>();
  const customOverrides = new Map<string, VenuePageNavItemRow>();
  const externalOverrides = new Map<string, VenuePageNavItemRow>();
  const groupOverrides = new Map<string, VenuePageNavItemRow>();

  for (const item of navItems) {
    switch (item.item_type) {
      case "base":
        baseOverrides.set(item.item_key, item);
        break;
      case "custom":
        customOverrides.set(item.item_key, item);
        break;
      case "external":
        externalOverrides.set(item.item_key, item);
        break;
      case "group":
        groupOverrides.set(item.item_key, item);
        break;
    }
  }

  // Process base items
  const baseKeys = Object.values(BASE_NAV_ITEM_KEYS) as BaseNavItemKey[];
  for (const key of baseKeys) {
    const item = baseOverrides.get(key);

    // Skip if explicitly hidden via item
    if (item?.is_hidden) {
      continue;
    }

    // Skip if not visible based on venue configuration
    if (!isBaseItemVisible(key, venuePage, hasFAQs, hasMenu, item)) {
      continue;
    }

    const config = BASE_NAV_ITEM_CONFIG[key];
    const position = item?.position ?? DEFAULT_BASE_NAV_POSITIONS[key];

    items.push({
      label: config.label,
      href: getBaseItemHref(key, slug, urlBase),
      title: config.title,
      type: "internal",
      position,
      itemType: "base",
      itemKey: key,
      parentGroupId: item?.parent_group_id ?? null,
    });
  }

  // Process custom links
  const basePath = getBasePath(urlBase, slug);
  customLinks.forEach((customLink, index) => {
    const itemKey = String(customLink.id);
    const item = customOverrides.get(itemKey);

    // Skip if explicitly hidden via item
    if (item?.is_hidden) {
      return;
    }

    // Skip content-override rows for built-in pages (FREE_SHOWS /
    // SHOWS_TONIGHT): they supply the built-in's title/body and render via the
    // base entry. Without this they double-render at the same href.
    if (isBuiltInPageKey(customLink.path)) {
      return;
    }

    // Skip if the page has no nav label - the page lives at its URL but the
    // admin chose not to surface it in the navbar.
    if (!customLink.label.trim()) {
      return;
    }

    const position =
      item?.position ?? DEFAULT_CUSTOM_LINK_START_POSITION + index;

    items.push({
      label: customLink.label,
      href: `${basePath}/${customLink.path}`,
      title: customLink.title,
      type: "internal",
      position,
      itemType: "custom",
      itemKey,
      parentGroupId: item?.parent_group_id ?? null,
    });
  });

  // Process external links
  externalLinks.forEach((externalLink, index) => {
    const itemKey = String(externalLink.id);
    const item = externalOverrides.get(itemKey);

    // Skip if explicitly hidden via item
    if (item?.is_hidden) {
      return;
    }

    const position =
      item?.position ?? DEFAULT_EXTERNAL_LINK_START_POSITION + index;

    items.push({
      label: externalLink.label,
      href: externalLink.url,
      type: "external",
      position,
      itemType: "external",
      itemKey,
      parentGroupId: item?.parent_group_id ?? null,
    });
  });

  // Process groups. A group only renders if it has a nav_items row that
  // isn't hidden. Position comes from that row. href is unused (the
  // collapsible header is the only thing that "exists" in the public UI).
  navGroups.forEach((group) => {
    const item = groupOverrides.get(group.id);
    if (!item || item.is_hidden) return;
    items.push({
      label: group.label,
      href: "",
      type: "internal",
      position: item.position,
      itemType: "group",
      itemKey: group.id,
      parentGroupId: null,
    });
  });

  // Sort by position
  items.sort((a, b) => a.position - b.position);

  return items;
}

/**
 * Folds a flat ComputedNavItem list into a 2-level tree: top-level entries
 * (parentGroupId === null) with each group's children attached in-position.
 * Children whose parent group does not exist (e.g. stale data) fall back to
 * the top level so they remain reachable.
 */
export function buildNavTree(items: ComputedNavItem[]): NavTreeNode[] {
  const topLevel: NavTreeNode[] = [];
  const childrenByGroup = new Map<string, ComputedNavItem[]>();
  const groupKeys = new Set<string>();

  for (const item of items) {
    if (item.itemType === "group") groupKeys.add(item.itemKey);
  }

  for (const item of items) {
    // Groups never nest: a group entry always stays top-level even if it
    // carries a parentGroupId (mirrors the DB no-nested-groups constraint).
    if (
      item.itemType !== "group" &&
      item.parentGroupId &&
      groupKeys.has(item.parentGroupId)
    ) {
      const bucket = childrenByGroup.get(item.parentGroupId) ?? [];
      bucket.push(item);
      childrenByGroup.set(item.parentGroupId, bucket);
    } else {
      topLevel.push({ ...item });
    }
  }

  for (const bucket of childrenByGroup.values()) {
    bucket.sort((a, b) => a.position - b.position);
  }

  return topLevel.map((node) =>
    node.itemType === "group"
      ? { ...node, children: childrenByGroup.get(node.itemKey) ?? [] }
      : node
  );
}

/**
 * Converts ComputedNavItem array to the format expected by PageNavMenu.
 * Dropdown groups are folded into a 2-level structure: top-level entries
 * appear as before; each group becomes a link with a populated `children`
 * array, which PageNavMenu renders as a desktop dropdown.
 */
export function toPageNavMenuLinks(items: ComputedNavItem[]): PageNavLink[] {
  const tree = buildNavTree(items);
  return tree.flatMap((node): PageNavLink[] => {
    if (node.itemType === "group") {
      const children = node.children ?? [];
      // An empty group has nothing to drop down into. Omit it rather than emit
      // a childless trigger, which the menu would otherwise render as a broken
      // link to the empty href.
      if (children.length === 0) return [];
      return [
        {
          id: `group:${node.itemKey}`,
          label: node.label,
          href: "",
          title: node.title,
          children: children.map((child) => ({
            label: child.label,
            href: child.href,
            title: child.title,
            type: child.type,
          })),
        },
      ];
    }
    return [
      {
        label: node.label,
        href: node.href,
        title: node.title,
        type: node.type,
      },
    ];
  });
}

/**
 * Returns a URL prefix for venue page routes. By default callers append segments
 * with a leading slash (e.g. `${basePath}/calendar`); the prefix never has a
 * trailing slash so the result has exactly one `/` between prefix and segment.
 *
 * - `/v/` + `"foo"` → `"/v/foo"` (e.g. `/v/foo/calendar`)
 * - `/venue/` + `"foo"` → `"/venue/foo"`
 * - `/` (producer / custom-domain page) → `""` (e.g. `/calendar`)
 *
 * Pass `{ trailingSlash: true }` when the prefix itself is the link target (e.g.
 * the venue's home page): returns `/v/foo/` for venue pages and `/` for producer
 * pages. Without this, the producer case returns `""`, which as an href resolves
 * to the current page rather than navigating home.
 */
export function getBasePath(
  urlBase: VenuePageUrlBase,
  slug: string,
  { trailingSlash = false }: { trailingSlash?: boolean } = {}
): string {
  const basePath = urlBase === "/" ? "" : `${urlBase}${slug}`;
  return trailingSlash ? `${basePath}/` : basePath;
}
